import { createContext, useContext } from "react";
import { Score } from "src/types";
import { ScoreType, useGetAllScoreType } from "src/api/client-api/scoreType/useGetAllScoreType";
import { Campaign, useGetAllCampaigns } from "src/api/client-api/useCampaign";
import { Slugs, Entities } from "./ProtectedContext";
import { CurrentUser, Company } from "src/api/client-api/useUserNew";
import type { ConsentGateItem } from "src/api/client-api/consent/useGetConsentGateItems";
import type { ConsentPreference } from "src/api/client-api/consent/useGetConsentPreferences";
import { JsonAttribute } from "src/api/client-api/jsonAttributes/useJsonAttribute";

export type CanUserCallback = (slug: Slugs, entity: Entities) => boolean;

// To avoid breaking change. But this interface is useless
export type UserContext = {
    trustedUser: CurrentUser;
};
export type JsonAttributesByEntity = Record<string, JsonAttribute[]>;
type ScoreTypesQueryResult = ReturnType<typeof useGetAllScoreType>;
type CampaignsQueryResult = ReturnType<typeof useGetAllCampaigns>;

export type FoodpilotContextInitialValues = {
    user: UserContext;
    pendingConsentItems: ConsentGateItem[] | undefined;
    pendingConsentPreferences: ConsentPreference[] | undefined;
    scoreTypes: ScoreType[] | undefined;
    scoreTypesError: ScoreTypesQueryResult["error"];
    scoreTypesLoading: boolean;
    scoreTypesStatus: ScoreTypesQueryResult["status"];
    retryScoreTypes: ScoreTypesQueryResult["refetch"];
    scores: Score[] | undefined;
    scoresLoading: boolean;
    campaigns: Campaign[];
    campaignsError: CampaignsQueryResult["error"];
    campaignsStatus: CampaignsQueryResult["status"];
    retryCampaigns: CampaignsQueryResult["refetch"];
    targetCampaign: Campaign | undefined;
    setCurrentCampaign: (value: Campaign | null) => void;
    currentCampaign: Campaign | null;
    canUser: CanUserCallback;
    isAdmin: () => boolean;
    userCompanies: Company[] | null;
    selectedCompany: Company | null;
    setSelectedCompany: (value: Company | null) => void;
    isUserOfTypeSupplier: () => boolean;
    getScoresByType: (type: ScoreType["type"]) => Score[];
    entitiesJsonAttributes: JsonAttributesByEntity | undefined;
};

// Entities
export const PRODUCT = "product";
export const PRODUCT_SETTINGS_ORGANIZATION = "product-settings-organization";
export const INGREDIENT = "ingredient";
export const PACKAGING = "packaging";
export const ORGANIZATION = "organization";
export const SUPPLIER = "supplier";
export const CAMPAIGN = "campaign";
export const SCORE_TYPE = "score-type";
export const PRODUCT_LINE = "product-line";
export const COMPANY_SECTOR = "company-sector";
export const STRATEGY = "strategy";
export const ACTION_PLAN = "action-plan";
export const ACTION_PLAN_TEMPLATE = "action-plan-template";
export const USER = "user";
export const ROLE = "role";
export const BRAND = "brand";
export const INGREDIENT_LINE = "ingredient-line";
export const INGREDIENT_SUPPLIER_ACTIVITY_DATA = "ingredient-supplier-activity-data";
export const PRODUCT_ACTIVITY_DATA = "product-activity-data";
export const SETTINGS = "settings";
export const TAG_TYPE = "tag-type";
export const MEDIA_CONTENT = "media-content";
export const PUCK_PAGE = "puck-page";
export const PUCK_EXTENSION = "puck-extension";
export const DASHBOARD = "dashboard";
export const JSON_ATTRIBUTE = "entity-json-attribute-definition";
export const ENTITY_TARGET_SCORE_ASSIGNMENT = "entity-target-score-assignment";
export const DATA_LIST_VALUE_OVERRIDE = "data-list-value-override";
export const NAVIGATION_MENU = "navigation-menu";
export const AUDIT_LOG = "audit-log";
export const LOCALE = "locale";
export const CUSTOM_THEME = "custom-theme";
export const COHORT_DEFINITION = "cohort-definition";

export const getAllObjects = () =>
    [
        PRODUCT,
        INGREDIENT,
        PACKAGING,
        ORGANIZATION,
        SUPPLIER,
        CAMPAIGN,
        SCORE_TYPE,
        PRODUCT_LINE,
        COMPANY_SECTOR,
        STRATEGY,
        ACTION_PLAN,
        ACTION_PLAN_TEMPLATE,
        USER,
        ROLE,
        BRAND,
        INGREDIENT_LINE,
        INGREDIENT_SUPPLIER_ACTIVITY_DATA,
        PRODUCT_ACTIVITY_DATA,
        SETTINGS,
        TAG_TYPE,
        MEDIA_CONTENT,
        PUCK_PAGE,
        PUCK_EXTENSION,
        DASHBOARD,
        JSON_ATTRIBUTE,
        ENTITY_TARGET_SCORE_ASSIGNMENT,
        DATA_LIST_VALUE_OVERRIDE,
        NAVIGATION_MENU,
        AUDIT_LOG,
        LOCALE,
        CUSTOM_THEME,
        COHORT_DEFINITION,
    ] as const;

// Slugs
export const VIEW = "view";
export const CREATE = "create";
export const MODIFY = "modify";
export const DELETE = "delete";
export const CHANGE = "change";
export const ASSIGN = "assign";
export const IMPERSONATE = "impersonate";
export const VIEW_SETTINGS = "view-settings";
export const VIEW_OVERVIEW = "view-overview";
export const VIEW_ORG_SETTINGS = "view-org-settings";
export const MODIFY_ORG_SETTINGS = "modify-org-settings";
export const ANSWER_SSQ = "answer-ssq";
export const VIEW_SSQ = "view-ssq";
export const VALIDATE_SSQ = "validate-ssq";
export const VIEW_SUPERSET_DASHBOARD = "view-superset-dashboard";
export const VIEW_NETWORK_TRACKING = "view-network-tracking";

export const getAllSlugs = () =>
    [
        VIEW,
        CREATE,
        MODIFY,
        DELETE,
        CHANGE,
        ASSIGN,
        IMPERSONATE,
        ANSWER_SSQ,
        VIEW_SSQ,
        VALIDATE_SSQ,
        VIEW_OVERVIEW,
        VIEW_ORG_SETTINGS,
        MODIFY_ORG_SETTINGS,
        VIEW_SUPERSET_DASHBOARD,
        VIEW_NETWORK_TRACKING,
    ] as const;

export const FoodpilotContext = createContext<FoodpilotContextInitialValues | null>(null);

export const useFoodpilotContext = () => {
    const context = useContext(FoodpilotContext);

    if (context) {
        return context;
    }

    throw new Error(`useProductListContext must be used within an ProtectedContextProvider`);
};
